import { useMemo } from "react";
import { PROJECT_TYPE_META } from "../../constants";

/**
 * Computes the "Project Journey" steps based on project type,
 * financial data, and task progress.
 */
export default function useJourneySteps({
  projectInfo,
  linkedPayComponents,
  linkedBills,
  linkedPayments,
  corpusDisbursements,
  primaryAccount,
  allProjects,
  actions,
}) {
  return useMemo(() => {
    if (!projectInfo) return [];
    const objType = projectInfo.projectType;
    const isFundRaise = objType === "fund-raise";

    // Compute corpus availability from primary account balance
    const reservedCorpus = (allProjects || [])
      .filter(
        (t) =>
          t.id !== projectInfo.id &&
          t.projectType === "corpus" &&
          t.status !== "resolved" &&
          t.status !== "closed" &&
          Number(t.estimatedBudget) > 0
      )
      .reduce((sum, t) => sum + Number(t.estimatedBudget || 0), 0);
    const totalCorpusBalance = Number(primaryAccount?.balance || 0);
    const availableCorpus = totalCorpusBalance - reservedCorpus;

    const steps = [
      {
        label: "Project Created",
        done: true,
        description: projectInfo.title,
      },
      {
        label: "Budget",
        done: true,
        description: projectInfo.estimatedBudget
          ? `₹${Number(projectInfo.estimatedBudget).toLocaleString()}`
          : "No budget set (optional)",
      },
      {
        label: "Classified",
        done: !!objType,
        description: (() => {
          if (objType) return PROJECT_TYPE_META[objType]?.label;
          const budget = Number(projectInfo.estimatedBudget) || 0;
          if (budget > 0 && budget > availableCorpus) {
            return `Budget ₹${budget.toLocaleString()} exceeds available corpus ₹${availableCorpus.toLocaleString()} — consider Fund Raise`;
          }
          if (budget > 0 && availableCorpus >= budget) {
            return `Available corpus ₹${availableCorpus.toLocaleString()} can cover this budget`;
          }
          return "Not yet classified";
        })(),
        action: !objType ? "classify" : null,
        corpusHint: { availableCorpus, reservedCorpus, totalCorpusBalance },
      },
    ];

    // Fund-raise needs settlement config, dues, and collections
    if (isFundRaise) {
      steps.push({
        label: "Settlement Config",
        done: linkedPayComponents.length > 0,
        description: linkedPayComponents.length
          ? `${linkedPayComponents.length} item(s) linked`
          : "Configure how costs are split among members",
        action: "setup-component",
        count: linkedPayComponents.length,
      });
      steps.push({
        label: "Dues Generated",
        done: (linkedBills.length + linkedPayments.length) > 0,
        description: (linkedBills.length + linkedPayments.length)
          ? `${linkedBills.length + linkedPayments.length} dues — ₹${[...linkedBills, ...linkedPayments]
              .reduce((s, b) => s + Number(b.amount || 0), 0)
              .toLocaleString()}`
          : "No dues generated yet",
        count: linkedBills.length + linkedPayments.length,
      });
      steps.push((() => {
        const totalDues = linkedBills.length + linkedPayments.length;
        const totalDuesAmount = [...linkedBills, ...linkedPayments].reduce((s, b) => s + Number(b.amount || 0), 0);
        const totalCollected = linkedPayments.reduce((s, p) => s + Number(p.amount || 0), 0);
        const pctAmount = totalDuesAmount > 0 ? Math.round((totalCollected / totalDuesAmount) * 100) : 0;

        const isRecurring = linkedPayComponents.some(
          (pc) => (pc.installmentType || pc.state?.installmentType) === "recurring"
        );

        if (!isRecurring) {
          const pctCount = totalDues > 0 ? Math.round((linkedPayments.length / totalDues) * 100) : 0;
          return {
            label: "Collections",
            done: totalDues > 0 && linkedPayments.length >= totalDues,
            progress: pctAmount,
            description: totalDues
              ? `${linkedPayments.length}/${totalDues} collected (${pctCount}%) — ₹${totalCollected.toLocaleString()} of ₹${totalDuesAmount.toLocaleString()} (${pctAmount}%)`
              : "No collections yet",
            count: linkedPayments.length,
          };
        }

        /* Recurring: group bills by cycle */
        const allBills = [...linkedBills, ...linkedPayments];
        const cycleMap = {};
        allBills.forEach((b) => {
          const d = b.end_date?.toDate ? b.end_date.toDate() : b.end_date?.seconds ? new Date(b.end_date.seconds * 1000) : new Date(b.end_date);
          const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
          if (!cycleMap[key]) cycleMap[key] = { total: 0, paid: 0, totalAmt: 0, paidAmt: 0 };
          cycleMap[key].total += 1;
          cycleMap[key].totalAmt += Number(b.amount || 0);
          if (b.transactionId) {
            cycleMap[key].paid += 1;
            cycleMap[key].paidAmt += Number(b.amount || 0);
          }
        });
        const cycles = Object.keys(cycleMap).sort();
        const completedCycles = cycles.filter((k) => cycleMap[k].paid >= cycleMap[k].total).length;
        const latestCycleKey = cycles[cycles.length - 1];
        const latestCycle = latestCycleKey ? cycleMap[latestCycleKey] : null;
        const latestPct = latestCycle && latestCycle.totalAmt > 0
          ? Math.round((latestCycle.paidAmt / latestCycle.totalAmt) * 100) : 0;
        const latestDone = latestCycle ? latestCycle.paid >= latestCycle.total : false;

        const fmtCycle = (key) => {
          const [y, m] = key.split("-");
          return new Date(y, Number(m) - 1).toLocaleDateString("en-IN", { month: "short", year: "numeric" });
        };

        const descParts = [];
        if (latestCycleKey) {
          descParts.push(
            `Current cycle (${fmtCycle(latestCycleKey)}): ${latestCycle.paid}/${latestCycle.total} — ₹${latestCycle.paidAmt.toLocaleString()} of ₹${latestCycle.totalAmt.toLocaleString()} (${latestPct}%)`
          );
        }
        descParts.push(`${completedCycles}/${cycles.length} cycle(s) fully collected`);
        descParts.push(`Overall: ₹${totalCollected.toLocaleString()} of ₹${totalDuesAmount.toLocaleString()} (${pctAmount}%)`);

        return {
          label: "Collections",
          done: latestDone,
          progress: latestPct,
          recurring: true,
          completedCycles,
          totalCycles: cycles.length,
          overallProgress: pctAmount,
          description: descParts.join(" · "),
          count: linkedPayments.length,
        };
      })());

      // Vendor payout step
      const activeDisbursementsFR = corpusDisbursements.filter((d) => d.status !== "cancelled");
      const totalDebitedFR = activeDisbursementsFR.reduce((s, d) => s + Number(d.amount || 0), 0);
      const budgetFR = Number(projectInfo.estimatedBudget) || 0;
      const pctFR = budgetFR > 0 ? Math.round((totalDebitedFR / budgetFR) * 100) : 0;
      steps.push({
        label: "Vendor Payout",
        done: budgetFR > 0 ? totalDebitedFR >= budgetFR : false,
        progress: budgetFR > 0 ? pctFR : undefined,
        overBudget: budgetFR > 0 && totalDebitedFR > budgetFR,
        description: activeDisbursementsFR.length
          ? `${activeDisbursementsFR.length} payout(s) — ₹${totalDebitedFR.toLocaleString()} spent${budgetFR > 0 ? ` of ₹${budgetFR.toLocaleString()} budget (${pctFR}%)` : " (no budget set)"}`
          : "No payouts recorded yet — go to the Disbursements tab to record one",
        action: "corpus-debit",
      });

      // Task progress for fund-raise
      const doneActionsFR = (actions || []).filter((a) => a.status === "done").length;
      const totalActionsFR = (actions || []).length;
      const actionPctFR = totalActionsFR > 0 ? Math.round((doneActionsFR / totalActionsFR) * 100) : 0;
      steps.push({
        label: "Task Progress",
        done: totalActionsFR > 0 && doneActionsFR >= totalActionsFR,
        progress: totalActionsFR > 0 ? actionPctFR : undefined,
        description: totalActionsFR > 0
          ? `${doneActionsFR}/${totalActionsFR} tasks done (${actionPctFR}%)`
          : "No tasks yet — create a Work Log with action items to track work",
      });
    } else if (objType === "no-cost") {
      const doneActions = (actions || []).filter((a) => a.status === "done").length;
      const totalActions = (actions || []).length;
      const actionPct = totalActions > 0 ? Math.round((doneActions / totalActions) * 100) : 0;
      steps.push({
        label: "Task Progress",
        done: totalActions > 0 && doneActions >= totalActions,
        progress: totalActions > 0 ? actionPct : undefined,
        description: totalActions > 0
          ? `${doneActions}/${totalActions} tasks done (${actionPct}%)`
          : "No tasks yet — create a Work Log with action items to track work",
      });
    } else if (objType === "corpus") {
      const activeDisbursements = corpusDisbursements.filter((d) => d.status !== "cancelled");
      const totalDebited = activeDisbursements.reduce((s, d) => s + Number(d.amount || 0), 0);
      const budgetCorpus = Number(projectInfo.estimatedBudget) || 0;
      const pctCorpus = budgetCorpus > 0 ? Math.round((totalDebited / budgetCorpus) * 100) : 0;
      steps.push({
        label: "Cheque Issue / Debit",
        done: budgetCorpus > 0 ? totalDebited >= budgetCorpus : false,
        progress: budgetCorpus > 0 ? pctCorpus : undefined,
        overBudget: budgetCorpus > 0 && totalDebited > budgetCorpus,
        description: activeDisbursements.length
          ? `${activeDisbursements.length} debit(s) — ₹${totalDebited.toLocaleString()} spent${budgetCorpus > 0 ? ` of ₹${budgetCorpus.toLocaleString()} budget (${pctCorpus}%)` : " (no budget set)"}`
          : "No disbursements recorded yet — go to the Disbursements tab to record one",
        action: "corpus-debit",
      });
      const doneActionsC = (actions || []).filter((a) => a.status === "done").length;
      const totalActionsC = (actions || []).length;
      const actionPctC = totalActionsC > 0 ? Math.round((doneActionsC / totalActionsC) * 100) : 0;
      steps.push({
        label: "Task Progress",
        done: totalActionsC > 0 && doneActionsC >= totalActionsC,
        progress: totalActionsC > 0 ? actionPctC : undefined,
        description: totalActionsC > 0
          ? `${doneActionsC}/${totalActionsC} tasks done (${actionPctC}%)`
          : "No tasks yet — create a Work Log with action items to track work",
      });
    }

    return steps;
  }, [projectInfo, linkedPayComponents, linkedBills, linkedPayments, corpusDisbursements, primaryAccount, allProjects, actions]);
}
